import { InteractionEvent, VerificationType } from '@logto/schemas';

import api from '@/apis/api';
import { storeState } from '@/utils/social-connectors';

import type { WechatAuthorization } from './ports';
import {
  identityOrigin,
  object,
  officialAuthorization,
  opaque,
  readWechatReference,
  referenceKey,
  type WechatFlow,
  type WechatReference,
} from './protocol';

const checkAborted = (signal: AbortSignal) => {
  if (signal.aborted) {
    throw new DOMException('Authentication cancelled', 'AbortError');
  }
};
const unavailable = () => new Error('WECHAT_PREPARATION_UNAVAILABLE');

/** Uses original Experience routes. No browser field can supply account/MFA facts. */
export function createNativeWechatAPI(deps: {
  connectorId: string;
  isCaptchaRequired: () => boolean;
  executeCaptcha: () => Promise<string | undefined>;
  setVerificationId: (type: VerificationType, id: string) => void;
}) {
  const clearReference = () => {
    try {
      const current = readWechatReference(sessionStorage.getItem(referenceKey));
      if (!current || current.connectorId === deps.connectorId) {
        sessionStorage.removeItem(referenceKey);
      }
    } catch {
      /* An unavailable optional hint never authenticates a user. */
    }
  };
  return {
    clearReference,
    async begin(
      flow: WechatFlow,
      initialize: boolean,
      signal: AbortSignal
    ): Promise<WechatAuthorization> {
      if (window.location.origin !== identityOrigin || !opaque(deps.connectorId)) {
        throw unavailable();
      }
      checkAborted(signal);
      if (initialize) {
        const captchaToken = await deps.executeCaptcha();
        checkAborted(signal);
        if (deps.isCaptchaRequired() && !captchaToken) {
          throw unavailable();
        }
        // The original initialization guard verifies CAPTCHA. The signal also
        // stops a late initialization from replacing a newly selected form.
        await api.put('/api/experience', {
          json: { interactionEvent: InteractionEvent.SignIn, captchaToken },
          signal,
        });
        checkAborted(signal);
      }
      const state = Array.from(crypto.getRandomValues(new Uint8Array(32)), (value) =>
        value.toString(16).padStart(2, '0')
      ).join('');
      const result = await api
        .post(`/api/experience/verification/social/${deps.connectorId}/authorization-uri`, {
          json: { state, redirectUri: `${identityOrigin}/callback/${deps.connectorId}` },
          signal,
        })
        .json<unknown>();
      checkAborted(signal);
      if (
        !object(result) ||
        Object.keys(result).slice().sort().join(',') !== 'authorizationUri,verificationId' ||
        !opaque(result.verificationId)
      ) {
        throw unavailable();
      }
      const authorization = officialAuthorization(result.authorizationUri, deps.connectorId, state);
      if (!authorization) {
        throw unavailable();
      }
      const reference: WechatReference = {
        revision: 1,
        connectorId: deps.connectorId,
        state,
        verificationId: result.verificationId,
        flow,
      };
      // These are original OAuth correlation and record references, not proof.
      // Code, authorization URL, identifiers, passwords and OTPs are not stored.
      storeState(state, deps.connectorId);
      sessionStorage.setItem(referenceKey, JSON.stringify(reference));
      deps.setVerificationId(VerificationType.Social, result.verificationId);
      return { reference, authorization };
    },
  };
}

/** Read before the original callback validation; a mismatch is never repaired. */
export function currentWechatReference(
  connectorId: string,
  state: string | undefined
): WechatReference | undefined {
  try {
    const value = readWechatReference(sessionStorage.getItem(referenceKey));
    return value?.connectorId === connectorId && value.state === state ? value : undefined;
  } catch {
    return undefined;
  }
}
export function consumeWechatReference(reference: WechatReference): void {
  try {
    const current = currentWechatReference(reference.connectorId, reference.state);
    if (current?.verificationId === reference.verificationId) {
      sessionStorage.removeItem(referenceKey);
    }
  } catch {
    /* No stored hint is a valid callback result. */
  }
}
